import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useAppSelector } from 'src/store/store';
import { createSearchRegExp } from 'src/utils/searchUtils';
import { setRestrictionListItems } from 'src/store/newJmrPageSlice';
import { RestrictionListItem } from 'src/utils/restrictionUtils';

/**
 * Select a "RestrictionItem" from a dropdown list.
 */
const AllTasksSelector = () => {
  const dispatch = useDispatch();
  const { restrictionListItems, consolidatedRegionTasks, isRestrictionsPageReadOnly } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const [tasksQuery, setTasksQuery] = useState<string>();
  const [dropdownValue, setDropdownValue] = useState<string[]>([]);
  const pauseUpdateDropdownValue = useRef(false);

  useEffect(() => {
    if (!pauseUpdateDropdownValue.current) {
      setDropdownValue(restrictionListItems.map((i) => i.label));
    } else {
      pauseUpdateDropdownValue.current = false;
    }
  }, [restrictionListItems]);

  // Format task to be used in the menu
  const formattedTaskOptions = useMemo(() => {
    return consolidatedRegionTasks
      .map((t) => ({ label: t.label, value: t.label, rank: t.rank }))
      .sort((a, b) => (a.rank !== null ? a.rank : Infinity) - (b.rank != null ? b.rank : Infinity));
  }, [consolidatedRegionTasks]);

  // Apply user entered filter string
  const matchedTaskOptions = useMemo(() => {
    if (!tasksQuery) {
      return formattedTaskOptions;
    }
    const taskSearchRegExp = createSearchRegExp(tasksQuery);
    return formattedTaskOptions.filter((option) => !tasksQuery || taskSearchRegExp.test(option.label));
  }, [formattedTaskOptions, tasksQuery]);

  // When the selection changes
  const onChange = (valueArray: string[]) => {
    pauseUpdateDropdownValue.current = true;
    setDropdownValue(valueArray);
    const map = new Map(restrictionListItems.map((i) => [i.label, i]));
    const updatedList: RestrictionListItem[] = [];
    valueArray.forEach((valueItem) => {
      const existingRestrictionListItem = map.get(valueItem);
      if (!!existingRestrictionListItem) {
        updatedList.push(existingRestrictionListItem);
      } else {
        const task = consolidatedRegionTasks.find((t) => t.label === valueItem);
        if (task) {
          updatedList.push(task);
        }
      }
    });
    dispatch(setRestrictionListItems(updatedList));
  };

  return (
    <Select
      data-cy="new-jmr__restrictions__all-tasks__dropdown-selector"
      data-testid="new-jmr__restrictions__all-tasks__dropdown-selector"
      searchQuery={tasksQuery}
      onSearch={(val) => setTasksQuery(val)}
      placeholder="Select task"
      value={dropdownValue}
      onChange={onChange}
      disabled={isRestrictionsPageReadOnly}
    >
      {matchedTaskOptions.map((option) => (
        <SelectOption
          label={option.label}
          value={option.value}
          key={option.value}
          data-cy="new-jmr__restrictions__all-tasks__dropdown-item"
        />
      ))}
      {!matchedTaskOptions.length && (
        <Column alignmentVertical="center" spacing="300" spacingInset="500">
          <Text alignment="center">No tasks found</Text>
        </Column>
      )}
    </Select>
  );
};

export default AllTasksSelector;
